import {history} from '@src/App'
import {RECORDS_ROUTES} from '@src/modules/Records/constants'
import {
  Button,
  ButtonGroup,
  Constants,
  Icon,
  Image,
  LightboxContextGroup,
  Loading,
  toastManager,
  useContainerDimensionsContext,
} from '@therms/atalaya'
import {
  Alerts,
  IdLabel,
  RPCDocs,
  RPCs,
  Tags,
  type DownloadPdfResult,
  useDownloadsApiServiceContext,
  useRPC,
} from '@therms/react-modules'
import {downloadBase64AsPdf} from '@therms/web-js'
import classNames from 'classnames'
import {useState} from 'react'
import {IoMdDownload} from 'react-icons/io'
import {IoClose} from 'react-icons/io5'
import {MdOpenInNew, MdRefresh} from 'react-icons/md'
import {generatePath} from 'react-router-dom'
import {DownloadRecordModal} from '../../router/screens/ViewRecord/components/DownloadRecordModal'
import {InfoTab} from './components/InfoTab'

interface ViewRecordProps {
  closeHandler: () => void
  recordId: string
}

function ViewRecord({closeHandler, recordId}: ViewRecordProps) {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const {
    viewport: {width: viewportWidth},
  } = useContainerDimensionsContext()

  const {client} = useDownloadsApiServiceContext()

  const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width

  const {call, data, isCalling, success} = useRPC<
    RPCDocs.Records.GetRecordProfile.v1.Args,
    RPCDocs.Records.GetRecordProfile.v1.Data
  >(RPCs.Records.GetRecordProfile.v1, {
    recordId,
  })

  const downloadRecord = (values) => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    client.records.DownloadRecord(
      {
        body: {
          recordId,
          showEventsRefs: values.showEventsRefs,
          showInternalContent: values.showInternalContent,
          showLocationsRefs: values.showLocationsRefs,
          showLogEntriesRefs: values.showLogEntriesRefs,
          showRecordsRefs: values.showRecordsRefs,
          showReportsRefs: values.showReportsRefs,
          withComments: values.withComments,
        },
      },
      {timeout: 60_000},
    )
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `Record ${data?.record.type} - ${data?.record.title}`,
            result.pdfBase64,
          )
        }
      })
      .catch((err: any) => {
        console.error('err downloading entry', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  if (!isCalling && !success)
    return (
      <div className="gap-x-xs text-color-critical inline-flex w-full items-center justify-center">
        <span>There was a problem loading</span>{' '}
        <Button onClick={call}>Retry</Button>
      </div>
    )

  if (!data) return <Loading overlay size="xxl" />

  const {record} = data

  return (
    <LightboxContextGroup files={record.fileUrls}>
      <div className={classNames({'pt-xs flex-col': isMobileLayout}, 'flex')}>
        <div
          className={classNames(
            {
              '-top-xl z-sticky pr-base pt-sm sticky w-64 shrink-0 self-start':
                !isMobileLayout,
            },
            'flex flex-col',
          )}
        >
          <div className="relative flex shrink-0 flex-col">
            {record.primaryImageUrl && (
              <Image
                className={classNames({
                  'min-h-[250px] max-w-[250px]': !isMobileLayout,
                  'mx-auto max-h-[250px] min-w-[250px]': isMobileLayout,
                })}
                fit="cover"
                fluid
                lightbox
                rounded
                src={record.primaryImageUrl}
              />
            )}

            {isMobileLayout && (
              <div className="bg-surface absolute right-0 top-0 rounded-bl">
                <ButtonGroup subtle size="sm">
                  <Button
                    onClick={() => setIsDownloadModalOpen(true)}
                    variant="main"
                  >
                    <Icon Icon={IoMdDownload} />
                  </Button>

                  <Button onClick={closeHandler} subtle size="sm">
                    <Icon Icon={IoClose} />
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </div>

          <div>
            <div className="mt-sm text-color-subtle text-sm font-medium capitalize">
              {record.type}
            </div>

            <div className="text-lg font-medium">
              {record.title}{' '}
              <IdLabel
                className="text-color-subtle text-base font-bold tracking-wide"
                idLabel={record.idLabel}
              />
            </div>
          </div>

          <div className={classNames(isMobileLayout ? 'mt-sm' : 'mt-lg')}>
            <Tags tags={record.tags} />
          </div>
        </div>

        <div
          className={classNames(
            {'border-border border-l': !isMobileLayout},
            'flex grow flex-col',
          )}
        >
          <div
            className={classNames({
              '-top-xl z-sticky space-x-sm bg-surface sticky flex items-start justify-end':
                !isMobileLayout,
            })}
          >
            {!isMobileLayout && (
              <ButtonGroup size="sm" subtle>
                <Button onClick={() => setIsDownloadModalOpen(true)}>
                  <Icon Icon={IoMdDownload} className="mr-xxs" />

                  <span>Download</span>
                </Button>

                <Button
                  className="cursor-pointer"
                  onClick={() => {
                    history.push(
                      generatePath(RECORDS_ROUTES.VIEW_RECORD, {
                        recordId,
                      }),
                    )
                    closeHandler()
                  }}
                  subtle
                  variant="main"
                >
                  <Icon Icon={MdOpenInNew} size="sm" />
                </Button>

                <Button onClick={closeHandler}>
                  <Icon Icon={IoClose} />
                </Button>
              </ButtonGroup>
            )}
          </div>

          <div
            className={classNames({'pl-base': !isMobileLayout}, 'pt-sm grow')}
          >
            <InfoTab record={record} />
          </div>
        </div>
      </div>

      {isDownloadModalOpen && (
        <DownloadRecordModal
          onDownload={(values) => {
            downloadRecord(values)
            setIsDownloadModalOpen(false)
          }}
          closeHandler={() => setIsDownloadModalOpen(false)}
        />
      )}
    </LightboxContextGroup>
  )
}

// used for code-splitting
// eslint-disable-next-line import-x/no-default-export
export default ViewRecord
export {ViewRecord}